// ABOUTME: SPEC-82 D-9 — the one voice-first bottom component both footers host: the mic CTA or the dots, the
// ABOUTME: glow ring, the tooltip and the revealed row, positioned OUTSIDE the clipping caption bar (trap T-2).

import React, { useCallback, useEffect, useId, useRef } from 'react';
import { u, uMin } from '../../ReelChrome/railGeometry';
import { mu, muMin } from '../../ReelChrome/mobileGeometry';
import { clusterSlotLeft, CLUSTER_DEFAULT_W, BAR_H_PCT } from '../../Projectionist/overlays/CaptionOverlay';
import { ARIA } from '../../../uiExperiment/copy';
import RevealedControls from './RevealedControls';
import WarmCore from './WarmCore';
import { RING, TALK, REVEAL, UNIT, OVERLAY_ALIGN, GLYPH } from './voiceDockTokens';
import './voiceDock.css';

const scaleOf = (composition) => (composition === 'desktop' ? u : mu);
const layoutOf = (composition) => (composition === 'desktop' ? 'row' : 'stacked');

/** The authored width of the slot the dots live in: the ring's diameter when there is a ring. */
export const dockSlotWidth = (composition, ring) => (ring ? RING.diameter : CLUSTER_DEFAULT_W[layoutOf(composition)]);

/**
 * The slot's box. Mobile: the authored square. Desktop: the authored diameter, but never taller than the bar (the footer wrapper's height is
 * the bar's) — on a laptop the bar is 53px tall while 176 authored px is 76px, and a ring taller than the bar hits the tooltip and row.
 */
const slotSize = (composition, width) => {
  if (composition !== 'desktop') return { width: mu(width), height: mu(width) };
  // The bar is BAR_H_PCT of the viewport (the footer portal is fixed inset-0), so vh states the same cap in a width too:
  // `aspect-ratio` on an absolutely positioned box falls back to its content width and turned the ring into a pill.
  const side = `min(${u(width)}, ${BAR_H_PCT}vh)`;
  return { width: side, height: side };
};

function DockRing({ reducedMotion }) {
  return (
    <div
      data-vf="ring"
      aria-hidden="true"
      className="vf-ring absolute rounded-full pointer-events-none"
      style={{
        inset: 0,
        border: `${RING.stroke}px solid ${RING.colour}`,
        boxShadow: [
          `0 0 ${RING.glowBlur * RING.coreGlowMul}px rgba(232,163,61,1)`,
          `0 0 ${RING.glowBlur}px rgba(232,163,61,${RING.glowAlpha})`,
          `0 0 ${RING.glowBlur * RING.haloMul}px rgba(232,163,61,${RING.haloAlpha})`,
          `inset 0 0 ${RING.glowBlur / 2}px rgba(232,163,61,${RING.glowAlpha / 2})`,
        ].join(', '),
        '--vf-ring-pulse-ms': `${RING.pulseMs}ms`,
        ...(reducedMotion ? { animation: 'none' } : {}),
      }}
    />
  );
}

function TalkButton({ talk, describedBy }) {
  return (
    <button
      type="button"
      data-vf="talk"
      aria-label={talk.label}
      aria-describedby={describedBy}
      onClick={talk.onTap}
      className="reel-touch-target relative flex items-center justify-center rounded-full"
      style={{ width: `${TALK.buttonFrac * 100}%`, height: `${TALK.buttonFrac * 100}%`, background: TALK.buttonBg }}
    >
      <img
        src={GLYPH.mic.src} alt="" aria-hidden="true"
        style={{ height: `${(TALK.glyphHeightFrac / TALK.buttonFrac) * 100}%`, aspectRatio: String(TALK.glyphAspect) }}
      />
    </button>
  );
}

/** The dots, clipped to the slot's circle: outside the caption bar nothing else crops the canvas's transparent margin. */
function Clipped({ children, scale = 1 }) {
  return (
    <div className="flex items-center justify-center" style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
      {scale === 1 ? children : (
        <div className="flex items-center justify-center" style={{ width: '100%', height: '100%', transform: `scale(${scale})` }}>{children}</div>
      )}
    </div>
  );
}

function DotsCore({ core, reveal, open, buttonRef, scale }) {
  if (!reveal?.enabled) return <Clipped scale={scale}>{core}</Clipped>;
  return (
    <button
      ref={buttonRef}
      type="button"
      data-vf="dots"
      aria-expanded={open}
      aria-label={open ? ARIA.hideControls : ARIA.showControls}
      onClick={reveal.onToggle}
      className="reel-touch-target relative flex items-center justify-center rounded-full"
      style={{ width: '100%', height: '100%' }}
    >
      <Clipped scale={scale}>{core}</Clipped>
    </button>
  );
}

/** The bubble over the CTA. Arrow points at the core: centred on desktop, at the slot centre on mobile. */
function Tooltip({ id, text, composition, slotLeft }) {
  const mobile = composition === 'mobile';
  const scale = scaleOf(composition);
  const font = mobile ? muMin(TALK.font, TALK.minPx) : uMin(TALK.font, TALK.minPx);
  return (
    <div
      id={id}
      role="tooltip"
      data-vf="tooltip"
      className="relative font-reel-medium text-reel-chrome-textPrimary whitespace-nowrap"
      style={{
        background: TALK.tooltipBg, fontSize: font, lineHeight: 1.15, textShadow: TALK.textGlow,
        padding: `${scale(TALK.tooltipPadY)} ${scale(TALK.tooltipPadX)}`, borderRadius: scale(TALK.tooltipRadius),
      }}
    >
      {text}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', top: '100%', left: mobile ? slotLeft : '50%', transform: 'translateX(-50%)',
          borderLeft: `${scale(TALK.arrow)} solid transparent`, borderRight: `${scale(TALK.arrow)} solid transparent`,
          borderTop: `${scale(TALK.arrow)} solid ${TALK.tooltipBg}`, width: 0, height: 0,
        }}
      />
    </div>
  );
}

/**
 * talk_label_right (phone only): the same bubble, beside the mic instead of over it — vertically centred on the slot, arrow pointing
 * left at the mic. `slotLeft` is the slot's CENTRE, so the bubble starts half a slot plus a gap to its right.
 */
function SideTooltip({ id, text, slotLeft, slotWidth, composition = 'mobile' }) {
  // Owner 2026-10-08: the 'Connecting to …' bubble uses this on desktop too (beside the ring, where the captions will appear), so it
  // takes the composition's own unit; the phone values are exactly the talk_label_right ones.
  const mobile = composition === 'mobile';
  const scale = scaleOf(composition);
  const font = mobile ? muMin(TALK.font, TALK.minPx) : uMin(TALK.font, TALK.minPx);
  return (
    <div
      id={id}
      role="tooltip"
      data-vf="tooltip"
      data-side="right"
      className="absolute font-reel-medium text-reel-chrome-textPrimary whitespace-nowrap"
      style={{
        '--u': UNIT[composition].core, left: `calc(${slotLeft} + ${slotWidth} / 2 + ${scale(TALK.sideGap)})`, top: '50%',
        transform: 'translateY(-50%)', pointerEvents: 'none',
        background: TALK.tooltipBg, fontSize: font, lineHeight: 1.15, textShadow: TALK.textGlow,
        padding: `${scale(TALK.tooltipPadY)} ${scale(TALK.tooltipPadX)}`, borderRadius: scale(TALK.tooltipRadius),
      }}
    >
      {text}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', right: '100%', top: '50%', transform: 'translateY(-50%)',
          borderTop: `${scale(TALK.arrow)} solid transparent`, borderBottom: `${scale(TALK.arrow)} solid transparent`,
          borderRight: `${scale(TALK.arrow)} solid ${TALK.tooltipBg}`, width: 0, height: 0,
        }}
      />
    </div>
  );
}

/**
 * SPEC-82 review L4: the CTA unmounts the moment it is tapped, which drops keyboard focus to <body>. A tap through the CTA hands
 * focus to the dots button that replaces it (when there is one and nothing else has taken focus); any other release leaves it alone.
 */
function useTapFocus(talk) {
  const dotsRef = useRef(null);
  const pendingRef = useRef(false);
  const onTap = talk?.onTap;
  const tapAndRefocus = useCallback(() => { pendingRef.current = true; onTap?.(); }, [onTap]);
  useEffect(() => {
    if (talk || !pendingRef.current) return;
    pendingRef.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) dotsRef.current?.focus();
  }, [talk]);
  return { dotsRef, tapAndRefocus };
}

/**
 * @param {object} p
 * @param {'desktop'|'mobile'} p.composition
 * @param {React.ReactNode} p.core the dots (the footer's own visualizer node); pre-tap it also draws, near-transparent, under the CTA
 * @param {boolean} [p.ring] glow_ring
 * @param {{label:string,onTap:Function,side?:boolean}|null} [p.talk] the pre-tap CTA; while set it replaces the dots (`side`: phone label beside it)
 * @param {{enabled:boolean,open:boolean,onToggle:Function}|null} [p.reveal] control_reveal (open by default; only the dots toggle it)
 * @param {Array} [p.controls] from buildDockControls(footerProps)
 * @param {boolean} [p.reducedMotion]
 * @param {string|null} [p.connecting] 'Connecting to …' BESIDE the ring while the agent starts (owner 2026-10-08: the controls row
 *   stays open; the caption space it sits in is empty until the agent's first words, which end it)
 */
export default function VoiceDock({
  composition, core, ring = false, talk = null, reveal = null, controls = [], reducedMotion = false, connecting = null,
}) {
  const tipId = useId();
  const { dotsRef, tapAndRefocus } = useTapFocus(talk);
  const scale = scaleOf(composition);
  const unit = UNIT[composition];
  const width = dockSlotWidth(composition, ring);
  const slotLeft = clusterSlotLeft(layoutOf(composition), ring ? RING.diameter : undefined);
  const open = !!reveal?.open && !talk;
  const centred = OVERLAY_ALIGN[composition] === 'center';
  const sideTip = !!talk?.side && composition === 'mobile'; // talk_label_right
  return (
    <div data-vf="dock" style={{ display: 'contents' }}>
      <div
        data-vf="core"
        className="absolute flex items-center justify-center"
        style={{
          '--u': unit.core, left: slotLeft, top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'auto',
          ...slotSize(composition, width),
        }}
      >
        {/* Keep-warm (video-lag fix): first in DOM order, so the ring and the opaque CTA both paint over it. Only while the CTA is up. */}
        {talk && core && <WarmCore><Clipped scale={ring ? RING.coreScale : 1}>{core}</Clipped></WarmCore>}
        {ring && <DockRing reducedMotion={reducedMotion} />}
        {talk ? <TalkButton talk={{ ...talk, onTap: tapAndRefocus }} describedBy={tipId} /> : (
          <DotsCore core={core} reveal={reveal} open={open} buttonRef={dotsRef} scale={ring ? RING.coreScale : 1} />
        )}
      </div>
      {talk && sideTip && <SideTooltip id={tipId} text={talk.label} slotLeft={slotLeft} slotWidth={slotSize(composition, width).width} />}
      {!talk && connecting && (
        <SideTooltip id={tipId} text={connecting} slotLeft={slotLeft} slotWidth={slotSize(composition, width).width} composition={composition} />
      )}
      <div
        data-vf="overlay"
        className="absolute"
        style={{
          '--u': unit.core, bottom: '100%', pointerEvents: 'none',
          ...(centred ? { left: slotLeft, transform: 'translateX(-50%)' } : { left: 0 }),
        }}
      >
        <div style={{ '--u': unit.controls, paddingBottom: scale(REVEAL.rowGap), pointerEvents: 'none' }}>
          {talk && !sideTip && <Tooltip id={tipId} text={talk.label} composition={composition} slotLeft={slotLeft} />}
          {reveal?.enabled && (
            <RevealedControls composition={composition} controls={controls} open={open} reducedMotion={reducedMotion} />
          )}
        </div>
      </div>
    </div>
  );
}
